import type { ScreenerColumn } from "@/services/api/screener";
import type { ExposureBandsMap } from "@/components/playground/adapters/echarts/types";
import {
  POPPINS_BOLD_FONT_CLASS,
  YANTRAMANAV_LIGHT_FONT_CLASS,
} from "@/fonts/reportBuilderFonts";

export const A4_DIMENSIONS_PX = {
  portrait: {
    width: 793,
    height: 1122,
  },
  landscape: {
    width: 1122,
    height: 793,
  },
};

/** Shared font family options used across report builder components */
export const FONT_FAMILIES = [
  { label: "Default", value: "font-sans" },
  { label: "Serif", value: "font-serif" },
  { label: "Mono", value: "font-mono" },
  { label: "Roboto", value: "font-roboto" },
  { label: "Golos Text", value: "font-golos-text" },
  { label: "Times New Roman", value: "font-times-new-roman" },
  { label: "Tiempos Headline", value: "font-tiempos-headline" },
  { label: "Degular", value: "font-degular" },
  { label: "Caveat", value: "font-caveat" },
  { label: "Poppins Bold", value: POPPINS_BOLD_FONT_CLASS },
  { label: "Yantramanav Light", value: YANTRAMANAV_LIGHT_FONT_CLASS },
] as const;

// Data Structures
export interface Filter {
  id: string;
  column: string;
  type: string;
  value: any;
  displayValue: string;
}

export interface CellOverride {
  content?: any;
}

export interface ColumnFormatting {
  /** Fixed column width in pixels (overrides Column Sizing tab for this column) */
  width?: number;
  /** Number of decimal places to display */
  decimal_places?: number;
  /** Display unit for scaling values */
  display_unit?: "as_is" | "thousands" | "millions" | "billions";
  /** Text to display before the value */
  prefix?: string;
  /** Text to display after the value */
  suffix?: string;
  /** Number format type */
  type?: "number" | "percentage" | "currency";
  /** Currency code (e.g., "USD", "GBP") for currency type */
  currency?: string;
  /** Text alignment override */
  alignment?: "left" | "center" | "right";
  /** Date format for date columns */
  date_format?:
    | "auto"
    | "YYYY-MM-DD"
    | "YY-MM-DD"
    | "MM-DD"
    | "DD/MM/YYYY"
    | "MM/DD/YYYY"
    | "DD MMM YYYY"
    | "MMM DD, YYYY"
    | "MMM YYYY"
    | "MMMM YYYY"
    | "Q[Q] YYYY";
}

export interface ReportItem {
  id: string;
  type: "text" | "chart" | "table" | "image" | "shape";
  content: any;
  position: { x: number; y: number };
  size: { width: number; height: number };
  backgroundColor?: string;
  color?: string;
  padding?: number;
  shape?: "circle" | "square" | "triangle" | "line";
  borderRadius?: number;
  rotation?: number;
  thickness?: number;
  borderEnabled?: boolean;
  fontSize?: number | string;
  headingLevel?: 1 | 2 | 3;
  fontFamilyClass?: string;
  presets?: Record<string, any>;
  isSized?: boolean;
  /** Used to control stacking order within the same area */
  zIndex?: number;
  /** Whether to constrain positioning within page bounds (default: true) */
  constrainToPageBounds?: boolean;
  /** @deprecated Use textConfig.columnCount instead */
  columnCount?: number; // number of columns
  /** @deprecated Use textConfig.columnGap instead */
  columnGap?: number; // px gap between columns
  /** Configuration for image-specific properties */
  imageConfig?: {
    objectFit?: "fill" | "contain" | "cover" | "none";
    lockAspectRatio?: boolean;
    sourceType?: "static" | "library" | "dynamic";
    assetId?: string;
    dynamicBinding?: {
      source:
        | "assigned_adviser"
        | "assigned_paraplanner"
        | "assigned_administrator";
      groupId: string;
      placeholderAssetId?: string;
    };
  };
  /** Configuration for text-specific properties */
  textConfig?: {
    /** Number of columns for multi-column text layout */
    columnCount?: number;
    /** Gap between columns in pixels */
    columnGap?: number;
    /** Vertical alignment of text within the box */
    verticalAlign?: "start" | "center" | "end";
  };
  /** Line style for shape lines: solid, dashed, dotted */
  lineStyle?: "solid" | "dashed" | "dotted";
  /** Custom dash pattern for dashed lines (array of dash and gap lengths) */
  dashPattern?: number[];
  /** Whether this item is locked from selection and editing */
  locked?: boolean;
  /** Opacity of the item (0-1, where 1 is fully opaque). Default: 1 */
  opacity?: number;
  tableConfig?: {
    /** Mode of table: basic (editable like MS Word) or data (fetches from API) */
    tableMode?: "basic" | "data";
    columns: string[];
    filters: Filter[];
    /** Static vs dynamic data source (UI hint) */
    dataSourceType?: "static" | "dynamic";
    /** Static-only: metadata for resources (displayName, type, stream) */
    resourceMetadata?: Record<string, any>;
    /** Dynamic-only: include benchmark row/series from parent context */
    dynamicIncludeBenchmark?: boolean;
    /** Dynamic-only: allow mixing in extra static resources alongside dynamic context */
    dynamicExtraResources?: string[];
    /** Dynamic-only: metadata for extra resources */
    dynamicExtraResourceMetadata?: Record<string, any>;
    /** Dynamic-only: colors for extra resources */
    dynamicExtraAssetColors?: Record<string, string>;
    /**
     * Array-based dynamic resource configuration for enhanced table data source selection
     * Each entry represents a dynamic resource type with its configuration
     */
    dynamicDataSources?: Array<{
      /** Type of dynamic resource: 'parent', 'benchmark', or custom type */
      type: "parent" | "benchmark" | string;
      /** Display label for this resource type */
      label: string;
      /** Color assigned to this resource type */
      color: string;
      /** Whether this resource type is enabled */
      enabled: boolean;
      /** Additional configuration for this resource type */
      config?: Record<string, any>;
    }>;
    /**
     * Dynamic resource index selection for tables
     * -1 = All enabled resources (for multi-resource comparison)
     * 0+ = Specific resource index from report config.resources array
     */
    dynamicResourceIndex?: number | number[];
    /** @deprecated Use dynamicDataSources instead */
    dynamicParentColor?: string;
    /** @deprecated Use dynamicDataSources instead */
    dynamicBenchmarkColor?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc" | "custom";
    secondarySortBy?: string;
    secondarySortOrder?: "asc" | "desc" | "custom";
    /** Per exposure type (snake_case), custom category order for sort */
    customSortOrders?: Record<string, string[]>;
    cellPadding?: number;
    borderColor?: string;
    tableBackgroundColor?: string;
    headerBackgroundColor?: string;
    headerTextColor?: string;
    footerBackgroundColor?: string;
    footerTextColor?: string;
    bodyTextColor?: string;
    alternateRowColor?: string;
    fontSize?: number | string;
    headerFontSize?: number | string;
    borderType?: "all" | "inside" | "inside_horizontal" | "inside_vertical" | "outside" | "none";
    /** Thickness of the main table borders in pixels (default: 1) */
    borderThickness?: number;
    /** Enables custom border settings for header cells only */
    headerBorderEnabled?: boolean;
    /** Border type used for header cells when headerBorderEnabled is true */
    headerBorderType?: "all" | "inside" | "inside_horizontal" | "inside_vertical" | "outside" | "none";
    /** Border color used for header cells when headerBorderEnabled is true */
    headerBorderColor?: string;
    /** Border thickness for header cells in pixels when headerBorderEnabled is true */
    headerBorderThickness?: number;
    watchlistId?: string;
    /** ID of the selected filtered list (for restoring selection when reopening dialog) */
    filteredListId?: string;
    /** Name of the selected filtered list (for display purposes) */
    filteredListName?: string;
    showBenchmarks?: boolean;
    /** Border style between resource and its benchmark row (when showBenchmarks is enabled) */
    benchmarkBorderStyle?: "full" | "dashed" | "none";
    /** Thickness of the benchmark border in pixels (default: 1) */
    benchmarkBorderThickness?: number;
    /** Minimum width for table columns in pixels */
    minColumnWidth?: number;
    /** Fixed width for the first column (typically name/label) in pixels */
    firstColumnWidth?: number;
    /** Fixed width for data columns (all columns after the first) in pixels */
    dataColumnWidth?: number;
    /** Whether to distribute data column widths evenly (respects first column width if set) */
    distributeColumnsEvenly?: boolean;
    /** Whether to transpose rows and columns in the table display */
    transpose?: boolean;
    /**
     * Bumps every time the user hits "Reset Data" so dependent hooks (useTableData)
     * can re-run their fetch logic without mutating any other config.
     */
    refreshKey?: number;
    cellOverrides?: { [rowId: string]: { [colId: string]: CellOverride } };
    headerOverrides?: { [colId: string]: CellOverride };
    /** Per-column formatting overrides (decimal places, units, prefix/suffix, etc.) */
    columnFormatting?: { [colId: string]: ColumnFormatting };
    page?: number;
    pageSize?: number;
    /** Toggle numeric heatmap cell styling */
    showHeatmap?: boolean;
    /** Number of decimal places to show for numeric values */
    decimalPlaces?: number;
    /**
     * Custom table type to pick backend data source. Defaults to "screener".
     * - "screener": Uses screener endpoint for data
     * - "holdings": Uses getHoldings endpoint for portfolio holdings
     * - "contributions": Uses getContributionsComparison endpoint for contribution analysis
     * - "exposures": Uses getExposuresComparison endpoint for exposure allocation tables
     */
    tableType?:
      | "screener"
      | "holdings"
      | "contributions"
      | "contributions_comparison"
      | "exposures";
    /** Time range configuration for contributions tables */
    contributionsTimeRange?: {
      preset?: string;
      start_date?: string;
      end_date?: string;
    };
    /** Holdings-specific options */
    holdingsEnableLookthrough?: boolean;
    holdingsAggregateLookthrough?: boolean;
    holdingsMaxLookthroughDepth?: number;
    /** When true, link holdings name cells to URLs from the holding_name_links feature flag */
    linkHoldingNames?: boolean;
    /** Contributions-specific lookthrough options */
    contributionsEnableLookthrough?: boolean;
    contributionsAggregateLookthrough?: boolean;
    contributionsMaxLookthroughDepth?: number;
    /** Exposure group to group contribution rows by (category rows instead of holdings) */
    contributionsGroupBy?: string;
    /** When grouping, show all categories from the exposure list */
    contributionsShowAllCategories?: boolean;
    /** Array of item names to exclude from the data results (case-insensitive matching) */
    exclude?: string[];
    /** Whether to allow text wrapping in table body cells (default: true) */
    wrapText?: boolean;
    /** Whether table header text is bold (default: true) */
    headerBold?: boolean;
    /** Whether to allow text wrapping in table header cells (default: false for nowrap) */
    headerWrapText?: boolean;
    /** Whether to show diff columns comparing each portfolio to the first (multi-portfolio only) */
    showDiff?: boolean;
    /** Exposures-specific: type of exposure (e.g., asset-allocation, sector, region) */
    exposureType?: string;
    /** Exposures-specific: variant to display (net, long, short) */
    exposureVariant?: "net" | "long" | "short";
  };
  chartConfig?: {
    chartType?: string;
    dataSourceId?: string;
    /**
     * Data source type for chart data assignment:
     * - "static": Direct resource selection (portfolios, products, benchmarks)
     * - "dynamic": Template mode using resource slots (unified dynamic/slots approach)
     *
     * Note: The old 3-concept system (static/dynamic/slots) has been simplified to 2 concepts.
     * "dynamic" now represents the unified template mode that handles both dynamic context
     * and resource slots.
     */
    dataSourceType?: "static" | "dynamic";
    /** Array of resource IDs chosen when dataSourceType === "static" */
    resources?: string[];
    /** Optional metadata for each resource (displayName, etc.) */
    resourceMetadata?: Record<string, any>;
    /** Optional colors for each resource */
    assetColors?: Record<string, string>;
    /** Optional watchlist metadata for UI display */
    watchlistMetadata?: {
      name?: string;
      id?: string;
    };
    /** Options specific to template mode (formerly dynamic data sources) */
    dynamicIncludeBenchmark?: boolean;
    dynamicIncludeSecondaryBenchmark?: boolean;
    /**
     * Array-based template mode resource configuration for scalability
     * Each entry represents a resource type with its color (used in template mode)
     */
    dynamicResourceColors?: Array<{
      /** Type of dynamic resource: 'parent', 'benchmark', 'secondary_benchmark', or custom type */
      type: "parent" | "benchmark" | "secondary_benchmark" | string;
      /** Display label for this resource type */
      label: string;
      /** Color assigned to this resource type */
      color: string;
      /** Whether this resource type is enabled */
      enabled: boolean;
    }>;
    /** @deprecated Use dynamicResourceColors instead */
    dynamicParentColor?: string;
    /** @deprecated Use dynamicResourceColors instead */
    dynamicBenchmarkColor?: string;
    /** @deprecated Use dynamicResourceColors instead */
    dynamicSecondaryBenchmarkColor?: string;
    /** Allow mixing additional static resources alongside template mode context */
    dynamicExtraResources?: string[];
    dynamicExtraResourceMetadata?: Record<string, any>;
    dynamicExtraAssetColors?: Record<string, string>;
    /**
     * Template mode resource index selection for charts
     * -1 = All enabled resources (for multi-resource comparison)
     * 0+ = Specific resource slot index from resourceSlots array
     * Can be a single number or array of numbers for multiple selection
     */
    dynamicResourceIndex?: number | number[];
    /** Array of additional resource slot IDs to compare with the base resource */
    comparisonResources?: string[];
    /**
     * Unified ordered list of chart data sources (hybrid mode).
     * When present, this takes precedence over legacy dataSourceType/resources/dynamicResourceIndex.
     * Allows mixing static resources and template slots in a user-defined order.
     */
    resourceEntries?: Array<{
      /** Entry type: 'static' for specific resources, 'slot' for template slots */
      type: "static" | "slot";
      /** For static: resource ID. For slot: unique identifier for the slot entry */
      id: string;
      /** Display name for the entry */
      displayName: string;
      /** Color for this entry */
      color?: string;
      /** For static resources: additional metadata */
      metadata?: {
        resourceType?: string;
        streamType?: string;
      };
      /** For slot entries: the slot index (0-based) */
      slotIndex?: number;
      /** For slot entries: whether to include benchmark for this slot */
      includeBenchmark?: boolean;
      /** For slot entries: whether to include secondary benchmark for this slot */
      includeSecondaryBenchmark?: boolean;
    }>;
    /** Cash Flow chart specific: ID of the cash flow plan to visualize */
    cashflowPlanId?: string;
    /** Cash Flow chart specific: ID of the scenario within the plan (defaults to baseline) */
    scenarioId?: string;
    /** Cash Flow chart specific: ID of the goal for analysis */
    goalId?: string;
    settings?: Partial<ChartSettings>;
  };
}

export interface ChartSettings {
  // high-level toggles
  showGrid?: boolean;
  // Lookthrough specific settings
  exposureType?: string;
  /** Exposure basis: "net" for net exposure splits, "primary" for primary category only */
  exposureBasis?: "net" | "primary";
  /** If true, show "Unclassified" bucket when exposure values don't sum to 100%. Default false. */
  showUnclassified?: boolean;
  /** Weight type: "actual" for current weights, "model" for model/target weights */
  weightType?: "actual" | "model";
  /** Snapshot as-at date for exposure pie/column charts (YYYY-MM-DD). Falls back to latest when unset. */
  asAtDate?: string;
  /** Min/max/target bands for exposure columns charts */
  exposureBands?: ExposureBandsMap;
  /** Exposure over time display mode */
  exposureDisplayMode?: "weights" | "scaled_weights" | "values";
  /** Exposure over time sampling frequency */
  exposureTimeseriesFrequency?: "monthly" | "quarterly";
  /** Correlation matrix return frequency */
  correlationFrequency?: "daily" | "weekly" | "monthly";
  /** Correlation matrix font size in pixels (headers, row labels, and cells) */
  correlationMatrixFontSize?: number;
  /** Correlation matrix data column width in pixels */
  correlationMatrixColumnWidth?: number;
  /** Correlation matrix row-label column width in pixels */
  correlationMatrixFirstColumnWidth?: number;
  /** Whether to group smaller items into "Other" category */
  groupSmallerItems?: boolean;
  /** Number of top items to show (rest grouped into "Other") */
  topNItems?: number;
  gridColor?: string;
  /** Whether to show grid lines in the chart */
  showGridLines?: boolean;
  /** Direct ECharts grid options (merged into option.grid) */
  grid?: Record<string, any>;
  /** Font size for axis labels in pixels */
  axisLabelFontSize?: number;
  /** Color for axis labels */
  axisLabelColor?: string;
  /** Whether to show X-axis line (default: true, zero line always shown when negative values exist) */
  showXAxisLine?: boolean;
  /** Whether to show Y-axis line (default: true) */
  showYAxisLine?: boolean;

  /** Performance chart specific settings */
  /** Display mode: "index" (rebased) or "performance" (percentage returns) */
  displayMode?: "index" | "performance";
  /** Base value for index display mode (default: 100) */
  baseValue?: number;
  /** Data frequency: daily, weekly, monthly, quarterly, yearly (with calendar or report-date-anchored variants) */
  frequency?: "daily" | "weekly" | "monthly" | "quarterly" | "quarterly_anchored" | "yearly" | "yearly_anchored";
  /** Whether to convert null values to zero in performance data (default: false) */
  convertNullToZero?: boolean;
  /** Whether to use smooth lines for line charts (default: true) */
  smoothLine?: boolean;
  /** Time period: how far back from report generation date to fetch data */
  timePeriod?:
    | "1M"
    | "3M"
    | "6M"
    | "YTD"
    | "1Y"
    | "3Y"
    | "5Y"
    | "10Y"
    | "MAX"
    | "ALL"
    | "CUSTOM";
  /** Start date for custom time period (ISO format: YYYY-MM-DD) */
  customStartDate?: string;
  /** End date for custom time period (ISO format: YYYY-MM-DD) */
  customEndDate?: string;
  /** X-axis label position offset in pixels */
  xAxisLabelOffset?: { x?: number; y?: number };
  /** Y-axis label position offset in pixels */
  yAxisLabelOffset?: { x?: number; y?: number };
  showXAxisTitle?: boolean;
  xAxisTitle?: string;
  /** X-axis title alignment: "start" (left), "middle" (center), "end" (right) */
  xAxisTitlePosition?: "start" | "middle" | "end";
  showYAxisTitle?: boolean;
  yAxisTitle?: string;
  /** Y-axis title alignment: "start" (bottom), "middle" (center), "end" (top) */
  yAxisTitlePosition?: "start" | "middle" | "end";
  /**
   * ECharts legend config. Prefer using this instead of showLegend for parity with
   * ECharts option names (https://echarts.apache.org/en/option.html#legend).
   */
  legend?: Record<string, any>;
  /** @deprecated Use legend.show instead */
  showLegend?: boolean;
  /** Legend layout: "horizontal" or "grid" */
  legendLayout?: "horizontal" | "grid";
  /** Number of columns for grid layout: "auto" | 1 | 2 */
  legendColumns?: "auto" | 1 | 2;
  /** Legend width as percentage (e.g., 80, 100) */
  legendWidth?: number;
  /** Legend icon type: "auto" (based on chart type), "circle", "rect", "roundRect", "triangle", "diamond", "pin", "arrow", "line", "none" */
  legendIcon?:
    | "auto"
    | "circle"
    | "rect"
    | "roundRect"
    | "triangle"
    | "diamond"
    | "pin"
    | "arrow"
    | "line"
    | "none";
  /** Screener field paths used as categories for categorical charts */
  categoricalFields?: string[];
  /** Orientation for category chart: column (vertical) or bar (horizontal) */
  categoricalStyle?: "column" | "bar";
  /** Optional overrides for category axis labels */
  categoryLabelOverrides?: Record<string, string>;
  /** Scatter chart X-axis statistic */
  xStatistic?: string;
  /** Scatter chart Y-axis statistic */
  yStatistic?: string;
  /** @deprecated Use xAxisTitle instead. Kept for backwards compatibility migration. */
  xLabel?: string;
  /** @deprecated Use yAxisTitle instead. Kept for backwards compatibility migration. */
  yLabel?: string;
  /** Whether to include resource labels on scatter chart points */
  includeLabels?: boolean;

  /** Pie chart specific settings */
  /** Pie chart style: "pie" for full circle, "donut" for donut chart */
  pieStyle?: "pie" | "donut";
  /** Inner radius for donut charts (0-100%) */
  innerRadius?: string | number;
  /** Outer radius for pie charts (0-100%) */
  outerRadius?: string | number;
  /** Whether to show labels on pie chart segments */
  showLabels?: boolean;
  /** Label position: "outside", "inside", "center" */
  labelPosition?: "outside" | "inside" | "center";
  /** Whether to show label lines connecting labels to segments */
  showLabelLines?: boolean;
  /** Pie chart center position [x%, y%] */
  center?: [string | number, string | number];
  /** Whether to show percentage in labels */
  showPercentage?: boolean;
  /** Whether to show percentages in legend items */
  showLegendPercentages?: boolean;
  /** Custom label formatter function or template */
  labelFormatter?: string;
  /** Legend position preset: determines placement relative to chart */
  legendPosition?: "bottom" | "top" | "left" | "right";
  /** Font size for legend text in pixels (default: 10) */
  legendFontSize?: number;
  /** Spacing between legend items in pixels (default: 10 for horizontal, 6 for grid) */
  legendItemGap?: number;
  /** Label format preset for legend items */
  legendLabelFormat?: "name" | "name-value" | "name-percent" | "custom";
  /** Custom format template when legendLabelFormat is "custom" (e.g., "{name}: {value}") */
  legendCustomFormat?: string;
  /** Gap/distance between legend and chart in pixels (default: 12 for left/right, 4 for top/bottom) */
  legendGap?: number;
  /** Text color for legend labels (hex color, e.g., "#1e293b") */
  legendTextColor?: string;
  /** Font family class for legend text (e.g., "font-sans", "font-roboto") */
  legendFontFamily?: string;

  /** Default color palette for chart series/data points */
  colorPalette?: string[];

  /** Platform boxes chart specific settings */
  /** Array of platform names to display in boxes */
  platforms?: string[];
  /** Column count for platform box grid layout; "auto" uses optimal distribution */
  platformColumns?: "auto" | 1 | 2 | 3 | 4 | 5 | 6;

  /** Comparison chart layout: "side-by-side", "grid", or "vertical" */
  comparisonLayout?: "side-by-side" | "grid" | "vertical";
  /** Whether to use consistent sizing for pie charts in comparison mode (default: true) */
  consistentComparisonSizing?: boolean;

  /** Time series chart event markers */
  events?: Array<{
    id: string;
    date: string;
    title: string;
  }>;

  /** Font size for event marker labels in pixels */
  eventLabelFontSize?: number;

  /** Axis granularity and scaling settings */
  /** X-axis granularity preset: auto, years, quarters, months, days */
  xAxisGranularity?: "auto" | "years" | "quarters" | "months" | "days";
  /** Y-axis scale type: auto, linear, logarithmic, custom */
  yAxisGranularity?: "auto" | "linear" | "log" | "custom";
  /** Custom X-axis tick interval (overrides granularity preset) */
  xAxisTickInterval?: number;
  /** Custom Y-axis tick interval (overrides granularity preset) */
  yAxisTickInterval?: number;
  /** Minimum number of X-axis ticks */
  xAxisMinTicks?: number;
  /** Maximum number of X-axis ticks */
  xAxisMaxTicks?: number;
  /** Minimum number of Y-axis ticks */
  yAxisMinTicks?: number;
  /** Maximum number of Y-axis ticks */
  yAxisMaxTicks?: number;
  /** Enable auto-scaling for X-axis */
  xAxisAutoScale?: boolean;
  /** Enable auto-scaling for Y-axis */
  yAxisAutoScale?: boolean;

  /** Risk Scale specific settings */
  /** Mode for Risk Scale: manual values or resource-based */
  riskScaleMode?: "manual" | "resource";
  /** Manual recommended value (1-10) */
  riskScaleRecommendedValue?: number;
  /** Manual current value (1-10) */
  riskScaleCurrentValue?: number;
  /**
   * Resource for recommended value (uses fundamental_data.risk_score)
   * Format: "slot:{slotId}" for dynamic slots, "static:{resourceId}" for static resources
   * Legacy format without prefix is treated as slot ID for backward compatibility
   */
  riskScaleRecommendedResource?: string;
  /**
   * Resource for current value (uses fundamental_data.risk_score)
   * Format: "slot:{slotId}" for dynamic slots, "static:{resourceId}" for static resources
   * Legacy format without prefix is treated as slot ID for backward compatibility
   */
  riskScaleCurrentResource?: string;
  /** Color for recommended indicator */
  riskScaleRecommendedColor?: string;
  /** Color for current indicator */
  riskScaleCurrentColor?: string;
  /** Background color for the Risk Scale component */
  riskScaleBackgroundColor?: string;
  /** Whether to show the shadow/ring around the scale (default: true) */
  riskScaleShowShadow?: boolean;
  /** Whether to hide the Recommended/Current labels and arrows (default: false) */
  riskScaleHideLabels?: boolean;
  /** Text color for the numbers in the scale boxes */
  riskScaleTextColor?: string;
  /** Text color for the Recommended/Current labels and arrows */
  riskScaleLabelColor?: string;

  /** Cash Flow chart specific settings */
  /** Chart style for cash flow projection: stacked bar or stacked area */
  cashflowChartStyle?: "stackedBar" | "stackedArea";
  /**
   * When true, fetch deterministic totals for every scenario and draw each as a line
   * (Cash Flow app "All scenarios" overlay).
   */
  cashflowShowAllScenarioTotals?: boolean;
  /**
   * Net worth vs cash flows timeline view (Cash Flow module). `netWorth` uses deterministic
   * asset stacks; `cashFlows` uses `values.cashflows` from the same forecast (see `buildCashFlowCashFlowsViewOption`).
   */
  cashflowChartView?: "netWorth" | "cashFlows";
  /**
   * Which assets appear in the stacked projection chart (net worth view): all assets vs goal-linked
   * assets only (matches Cash Flow **All assets** / **Goal assets**).
   */
  cashflowAssetFilter?: "all" | "goal";
  /**
   * In and out vs net cash flows (only applies when `cashflowChartView` is `cashFlows`).
   */
  cashflowCashFlowMode?: "inOutflows" | "net";
  /**
   * X-axis category labels for cash flow charts (matches Cash Flow module toolbar).
   * Defaults to the plan’s `x_axis_format` when unset.
   */
  cashflowXAxisLabelFormat?: "year" | "age" | "calendar";
  /** Overrides plan `client_age` for age-based x labels. */
  cashflowXAxisClientAge?: number;
  /**
   * Per-period goal met pills below the stacked bars (Cash Flow module parity).
   * Default true when a goal is set.
   */
  cashflowShowGoalPeriodIndicators?: boolean;
  /** Whether to show the goal line on cash flow charts */
  showGoalLine?: boolean;
  /** Whether to show the total line on cash flow projection charts */
  showTotalLine?: boolean;
  /** Whether to show individual asset breakdown in stacked charts */
  showAssetBreakdown?: boolean;
  /** Whether to show the median line on fan charts */
  showMedianLine?: boolean;
  /** Whether to show the outer (10-90) percentile band on fan charts */
  showOuterBand?: boolean;
  /** Whether to show the inner (25-75) percentile band on fan charts */
  showInnerBand?: boolean;
  /** Color for goal line on cash flow charts */
  goalLineColor?: string;
  /** Color for median line on fan charts */
  medianLineColor?: string;
  /** Color for percentile bands on fan charts */
  bandColor?: string;

  /**
   * Field path for custom series names from resource snapshot data.
   * When set, fetches this field (e.g., "fundamental_data.saltus_birchwood_fund_name")
   * for each resource and uses it as the series name in charts.
   * Only available when the "custom_series_names" feature flag is enabled.
   */
  seriesNameField?: string;

  // raw ECharts option override (merged last)
  optionOverrides?: Record<string, any>;
}

/** Normalize risk scale mode; legacy worker decks may emit "value" for manual integers. */
export function getRiskScaleMode(
  mode?: ChartSettings["riskScaleMode"] | "value",
): "manual" | "resource" {
  return mode === "resource" ? "resource" : "manual";
}

/** The user who authored a comment. */
export interface CommentAuthor {
  /** User ID */
  id: string;
  /** Display name */
  name: string;
  /** Email address */
  email?: string;
}

/** Review / presentation comment status for accept-and-correct workflows. */
export type PageCommentStatus = "open" | "done";

/**
 * A comment on a presentation slide.
 * - Pin: `x`/`y` only (percent of slide)
 * - Highlight: `x`/`y` top-left plus `w`/`h` (percent of slide)
 */
export interface PageComment {
  /** Unique identifier */
  id: string;
  /** Horizontal position as a percentage (0–100) of the slide width */
  x: number;
  /** Vertical position as a percentage (0–100) of the slide height */
  y: number;
  /** Optional highlight width as a percentage of slide width */
  w?: number;
  /** Optional highlight height as a percentage of slide height */
  h?: number;
  /** Comment body text */
  text: string;
  /** User who created the comment */
  author?: CommentAuthor;
  /** ISO timestamp of when the comment was created */
  createdAt?: string;
  /** open = needs attention; done = addressed / accepted */
  status?: PageCommentStatus;
  /**
   * Optional follow-up note (e.g. what was changed when marking done).
   * Shown in the comments list for both open and completed items.
   */
  resolution?: string;
}

export interface Page {
  id: string;
  name: string;
  items: ReportItem[];
  size: { width: number; height: number }; // in px
  isTitlePage?: boolean;
  padding?: number;
  backgroundColor?: string;
  backgroundImage?: string;
  /** Base64 data URL of a lightweight thumbnail preview captured from the canvas */
  thumbnail?: string;
  /** Internal flag to gate thumbnail recapture; not persisted to API */
  thumbnailDirty?: boolean;
  /** Comments pinned / highlighted on this slide in presenter / review mode */
  comments?: PageComment[];
}

export interface SavedReport {
  id: string;
  name: string;
  createdAt: string;
  data: {
    pages: Page[];
    headerItems: ReportItem[];
    footerItems: ReportItem[];
  };
}

/** Default columns for new holdings tables */
export const DEFAULT_HOLDINGS_COLUMNS = ["name", "weight"];

/** Holdings-specific columns available for Portfolio Holdings tables */
export const HOLDINGS_COLUMNS: ScreenerColumn[] = [
  {
    id: "name",
    name: "Name",
    type: "string",
    filterable: true,
    sortable: true,
    group: "Holdings",
    description: "Holding name",
  },
  {
    id: "weight",
    name: "Weight",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Holdings",
    description: "Current weight percentage",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "real_weight",
    name: "Real Weight",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Holdings",
    description: "Target/model weight percentage",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "effective_weight",
    name: "Effective Weight",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Holdings",
    description: "Effective weight after lookthrough",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "effective_real_weight",
    name: "Effective Real Weight",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Holdings",
    description: "Effective real/model weight after lookthrough",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "drift",
    name: "Drift",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Holdings",
    description:
      "Effective actual weight minus effective model weight (matches portfolio holdings); without lookthrough, current weight minus target",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "lookthrough_depth",
    name: "Lookthrough Depth",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Holdings",
    description: "Depth level in lookthrough hierarchy",
  },
  {
    id: "is_portfolio_holding",
    name: "Is Portfolio Holding",
    type: "boolean",
    filterable: true,
    sortable: true,
    group: "Holdings",
    description: "Whether this is a direct portfolio holding",
  },
];

/** Default columns for new contributions tables */
export const DEFAULT_CONTRIBUTIONS_COLUMNS = ["name", "contribution"];

/** Contributions-specific columns available for Portfolio Contributions tables */
export const CONTRIBUTIONS_COLUMNS: ScreenerColumn[] = [
  {
    id: "name",
    name: "Security",
    type: "string",
    filterable: true,
    sortable: true,
    group: "Contributions",
    description: "Holding name (display name with long/short fallbacks)",
    formatting: { alignment: "left" },
  },
  {
    id: "base_currency",
    name: "Base Currency",
    type: "string",
    filterable: true,
    sortable: true,
    group: "Fundamentals",
    description: "Holding base currency at end of period",
  },
  {
    id: "short_name",
    name: "Short Name",
    type: "string",
    filterable: true,
    sortable: true,
    group: "Fundamentals",
    description: "Short name of the holding",
    formatting: { alignment: "left" },
  },
  {
    id: "returns_as_of",
    name: "Data as of",
    type: "date",
    filterable: true,
    sortable: true,
    group: "Fundamentals",
    description:
      "Latest date the holding stream has REAL total return data for",
    formatting: { alignment: "left" },
  },
  {
    id: "avg_weight",
    name: "Avg Weight (%)",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Weights",
    description: "Average weight over the period (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "start_weight",
    name: "Start Wgt (%)",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Weights",
    description: "Weight at the start of the period (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "end_weight",
    name: "End Wgt (%)",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Weights",
    description: "Weight at the end of the period (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "start_value_portfolio_ccy",
    name: "Start Value",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Weights",
    description: "Holding value at the start of the period",
    formatting: { type: "currency", decimal_places: 2 },
  },
  {
    id: "end_value_portfolio_ccy",
    name: "End Value",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Weights",
    description: "Holding value at the end of the period",
    formatting: { type: "currency", decimal_places: 2 },
  },
  {
    id: "weight",
    name: "Curr Wgt (%)",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Weights",
    description: "Current weight (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "contribution",
    name: "Contrib (%)",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Contribution",
    description: "Contribution to portfolio return (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "cumulative_local_return",
    name: "Local (%)",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Performance",
    description: "Cumulative local currency return (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "cumulative_fx_return",
    name: "FX (%)",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Performance",
    description: "Cumulative FX return (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "cumulative_portfolio_return",
    name: "Total (%)",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Performance",
    description: "Cumulative total return (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "cumulative_benchmark_return",
    name: "Bench (%)",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Performance",
    description: "Cumulative benchmark return (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "cumulative_relative_return",
    name: "Relative (%)",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Performance",
    description: "Cumulative relative return (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
  {
    id: "relative_contribution",
    name: "Rel. Contrib (%)",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Performance",
    description: "Relative contribution to portfolio return (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
];

/** Time period options for performance history charts in report builder */
export const PERFORMANCE_CHART_TIME_PERIOD_OPTIONS = [
  { value: "1M", label: "1 Month" },
  { value: "3M", label: "3 Months" },
  { value: "6M", label: "6 Months" },
  { value: "YTD", label: "Year to Date" },
  { value: "1Y", label: "1 Year" },
  { value: "3Y", label: "3 Years" },
  { value: "5Y", label: "5 Years" },
  { value: "10Y", label: "10 Years" },
  { value: "MAX", label: "Maximum (Common)" },
  { value: "ALL", label: "Maximum (All)" },
  { value: "CUSTOM", label: "Custom" },
] as const;

/** Time range presets for contributions tables */
export const TIME_RANGE_PRESETS = [
  { value: "MTD", label: "Month to Date" },
  { value: "QTD", label: "Quarter to Date" },
  { value: "YTD", label: "Year to Date" },
  { value: "custom", label: "Custom Date Range" },
  { value: "1Y_PME", label: "1 Year (Prev Month End)" },
  { value: "3Y_PME", label: "3 Years (Prev Month End)" },
  { value: "5Y_PME", label: "5 Years (Prev Month End)" },
  { value: "1Y", label: "1 Year" },
  { value: "3Y", label: "3 Years" },
  { value: "5Y", label: "5 Years" },
  { value: "MAX", label: "Since Inception" },
];

/** Default columns for new exposures tables */
export const DEFAULT_EXPOSURES_COLUMNS = ["category", "value"];

/** Exposures-specific columns available for Portfolio Exposures tables */
export const EXPOSURES_COLUMNS: ScreenerColumn[] = [
  {
    id: "category",
    name: "Category",
    type: "string",
    filterable: true,
    sortable: true,
    group: "Exposures",
    description: "Exposure category (e.g., asset class, sector)",
  },
  {
    id: "value",
    name: "Weight",
    type: "number",
    filterable: true,
    sortable: true,
    group: "Exposures",
    description: "Allocation weight (%)",
    formatting: { type: "percentage", decimal_places: 2 },
  },
];
